import { actions as dynamicEntryActions } from '../actions/actions.dynamic-entry';
import EventBus from '../services/service.eventBus';
import { state as dynamicEntryState } from '../store/store.dynamic-entry';
import { trackStartMyOfferError } from '../tracking/tracking.dynamic-entry';
import { LICENSE_PLATE_ERRORS, MSM_MILEAGE_ERRORS, MSM_STYLE_ERRORS, VIN_ERRORS, VIN_LENGTH, YMM_ERRORS } from '../utils/utils.dynamic-entry';
import { MAX_MILEAGE } from '../utils/utils.vehicle';
import { isValidVin } from '../utils/utils.vin';

const onValidateVinEntry = ({ isNotFound = false } = {}) => {
  try {
    return onValidateVinEntrySuccess({ isNotFound });
  } catch (error) {
    onValidateVinEntryFail(error);
  }
};

const onValidateVinActiveDealerListingEntry = () => {
  try {
    onValidateVinActiveDealerListingEntrySuccess();
  } catch (error) {
    onValidateVinActiveDealerListingEntryFail(error);
  }
};

const onValidateVinActiveDealerListingEntrySuccess = () => {
  const { inputVin, enableMakeModel } = dynamicEntryState;
  EventBus.publish('onValidateVinActiveDealerListingEntry', { vin: inputVin, enableMakeModel });

  dynamicEntryState.vinError = VIN_ERRORS.VIN_ACTIVE_AUTOTRADER_LISTING_ERROR;
  dynamicEntryState.hasVinActiveAutotraderListingError = true;

  EventBus.publish('onValidateVinActiveDealerListingEntrySuccess', true);
};

const onValidateVinActiveDealerListingEntryFail = (error) => {
  EventBus.publish('onValidateVinActiveDealerListingEntryFail', error);
};

const onValidateVinEntrySuccess = ({ isNotFound = false } = {}) => {
  const { inputVin, enableMakeModel } = dynamicEntryState;
  EventBus.publish('onValidateVinEntry', { vin: inputVin, isNotFound, enableMakeModel });

  let isValid = false;
  if (!inputVin || inputVin.length < VIN_LENGTH || (inputVin.length === VIN_LENGTH && !isValidVin(inputVin))) {
    dynamicEntryState.vinError = VIN_ERRORS.VIN_LENGTH_ERROR;
    trackStartMyOfferError('vin_error_vin_char');
  } else if (inputVin !== '' && isNotFound) {
    dynamicEntryState.vinError = getVinErrorIsNotFoundMsg(enableMakeModel);
  } else {
    dynamicEntryState.vinError = '';
    isValid = true;
  }

  dynamicEntryState.hasVinError = !isValid;
  dynamicEntryActions.onSetHasSecondaryYmmOption();
  EventBus.publish('onValidateVinEntrySuccess', isValid);
  return isValid;
};

const onValidateVinEntryFail = (error) => {
  EventBus.publish('onValidateVinEntryFail', error);
};

const getVinErrorIsNotFoundMsg = (enableMakeModel: boolean = true) => {
  if (enableMakeModel) {
    return VIN_ERRORS.VIN_NOT_FOUND_ERROR;
  }
  return VIN_ERRORS.VIN_NOT_FOUND_ERROR_2_OPTIONS;
};

const onValidatePlateEntry = ({ hasChanged = false } = {}): boolean => {
  try {
    return onValidatePlateEntrySuccess({ hasChanged });
  } catch (error) {
    onValidatePlateEntryFail(error);
  }
};

const onValidatePlateEntrySuccess = ({ hasChanged = false }): boolean => {
  const { licensePlateNumber, licensePlateState, enableMakeModel } = dynamicEntryState;
  EventBus.publish('onValidatePlateEntry', { licensePlateNumber, licensePlateState, enableMakeModel, hasChanged });

  let isValid = false;
  if (hasChanged) {
    dynamicEntryState.lpError = '';
    isValid = true;
  } else if (licensePlateNumber === '' || licensePlateState === '') {
    dynamicEntryState.lpError = LICENSE_PLATE_ERRORS.PLATE_MISSING;
  } else {
    dynamicEntryState.lpError = getPlateNotFoundMsg(enableMakeModel);
    trackStartMyOfferError('lp_error');
  }

  dynamicEntryState.hasLPError = !isValid;
  dynamicEntryActions.onSetHasSecondaryYmmOption();
  EventBus.publish('onValidatePlateEntrySuccess', isValid);
  return isValid;
};

const onValidatePlateEntryFail = (error) => {
  EventBus.publish('onValidatePlateEntryFail', error);
};

const getPlateNotFoundMsg = (enableMakeModel: boolean = true) => {
  if (enableMakeModel) {
    return LICENSE_PLATE_ERRORS.PLATE_NOT_FOUND_ASSIGNED;
  }
  return LICENSE_PLATE_ERRORS.PLATE_NOT_FOUND_UNASSIGNED;
};

const onValidateYmmEntry = (): boolean => {
  try {
    return onValidateYmmEntrySuccess();
  } catch (error) {
    onValidateYmmEntryFail(error);
  }
};

const onValidateYmmEntrySuccess = (): boolean => {
  const { hasYmmError } = dynamicEntryState;
  EventBus.publish('onValidateYmmEntry', hasYmmError);

  let isValid = false;
  if (hasYmmError) {
    dynamicEntryState.ymmError = YMM_ERRORS.YMM_MISSING;
    trackStartMyOfferError('mm_error');
  } else {
    dynamicEntryState.ymmError = '';
    isValid = true;
  }

  dynamicEntryState.hasYmmError = !isValid;
  dynamicEntryActions.onSetHasSecondaryYmmOption();
  EventBus.publish('onValidateYmmEntrySuccess', isValid);
  return isValid;
};

const onValidateYmmEntryFail = (error) => {
  EventBus.publish('onValidateYmmEntryFail', error);
};

const onValidateStyle = (): boolean => {
  const { trimId, hasMultipleTrims } = dynamicEntryState;
  const hasTrimId = trimId > 0;

  let isValid = false;
  if (hasMultipleTrims && !hasTrimId) {
    dynamicEntryState.styleError = MSM_STYLE_ERRORS.MSM_STYLE_MISSING;
  } else {
    dynamicEntryState.styleError = '';
    isValid = true;
  }
  dynamicEntryState.hasStyleError = !isValid;
  dynamicEntryActions.onSetHasSecondaryYmmOption();
  return isValid;
};

const onValidateMileage = (): boolean => {
  const { mileage } = dynamicEntryState;
  const isAssignedOrCobranding = dynamicEntryActions.isAssignedOrCobrandingFlow();
  
  let isValid = false;
  if (!mileage) {
    dynamicEntryState.mileageError = MSM_MILEAGE_ERRORS.MSM_MILEAGE_MISSING;
  } else if (mileage > MAX_MILEAGE && !isAssignedOrCobranding) {
    dynamicEntryState.mileageError = MSM_MILEAGE_ERRORS.MSM_INVALID_MILEAGE;
  } else {
    dynamicEntryState.mileageError = '';
    isValid = true;
  }
  dynamicEntryState.hasMileageError = !isValid;
  dynamicEntryActions.onSetHasSecondaryYmmOption();
  return isValid;
};

const actions = {
  onValidateVinEntry,
  onValidatePlateEntry,
  onValidateYmmEntry,
  onValidateStyle,
  onValidateMileage,
  onValidateVinActiveDealerListingEntry,
};

export { actions };
export default actions;
